import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation } from '@apollo/client';
import { Link } from 'react-router-dom';
import { useAccount } from '../../context/AccountContext';
import { PUBLISH_TINYCMS_CONTENT, UNPUBLISH_TINYCMS_CONTENT } from '../../graphql';
import { showInkToast } from './InkToast';
import {
  Span,
  TodayApproval,
  TodayCampaign,
  TodayKind,
  TodayScheduled,
  TodayWaiting,
  useToday,
} from './todayData';
import {
  APPROVE_CAMPAIGN,
  APPROVE_SOCIAL_POST,
  channelName,
  postText,
  postTime,
  ReachPost,
  toDate,
} from '../reach/reach';

/**
 * TinyCMS → Today ("TinyCMS UX optimization review v9", Prototype v6). The
 * default page: how much changed, what waits on the person — approvable from
 * its card — what went out (undoable while it is still live), and what is
 * scheduled. Built from tinycmsToday (approvals, the agent's edits from the
 * MCP log, checks) and Reach's calendar and campaigns.
 */

const DAY = 86400000;
const QUEUE_SHOWN = 8;

const time = (d: Date) => d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
const weekday = (d: Date) => d.toLocaleDateString([], { weekday: 'short' });

function dayLabel(date = new Date()) {
  return date.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });
}

/** "22m", "3h", "2d" — how long something has waited. */
function ago(iso?: string | null) {
  const d = toDate(iso);
  if (!d) return '';
  const min = Math.max(0, Math.round((Date.now() - d.getTime()) / 60000));
  if (min < 60) return `${min}m`;
  const h = Math.round(min / 60);
  return h < 24 ? `${h}h` : `${Math.round(h / 24)}d`;
}

/** "Today 6:00 PM", "Tomorrow 9:00 AM", "Sun 9:00 AM", "Oct 30, 9:00 AM". */
function when(d: Date, now = new Date()) {
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  const days = Math.floor((d.getTime() - start.getTime()) / DAY);
  if (days === 0) return `Today ${time(d)}`;
  if (days === 1) return `Tomorrow ${time(d)}`;
  if (days > 1 && days < 7) return `${weekday(d)} ${time(d)}`;
  return `${d.toLocaleDateString([], { month: 'short', day: 'numeric' })}, ${time(d)}`;
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

const reviewHref = (kind: TodayKind | 'dataset', id: string) =>
  kind === 'article'
    ? `/content/${id}`
    : kind === 'page'
      ? `/content?view=pages&page=${id}`
      : kind === 'form'
        ? `/content?view=forms&form=${id}`
        : '/content?view=design';

const VERB: Record<string, string> = {
  publish: 'Published',
  schedule: 'Scheduled',
  unpublish: 'Took down',
};
const WHAT: Record<string, string> = {
  article: 'an article',
  page: 'a page',
  form: 'a form',
  site: 'the site’s look and navigation',
  dataset: 'a dataset',
};

/** "Took down “Keratoconus management”", else by kind ("Published a page · version 2"). */
function describeApproval(r: TodayApproval) {
  const verb = VERB[r.action] || r.action;
  if (r.subjectTitle) return `${verb} “${r.subjectTitle}”`;
  const what = WHAT[r.kind] || r.kind;
  return `${verb} ${what}${r.versionNumber ? ` · version ${r.versionNumber}` : ''}`;
}

// ── Ribbon ──────────────────────────────────────────────────────────────────

type Tone = 'published' | 'edited' | 'waiting' | 'scheduled' | 'down';
const TONE_COLOR: Record<Tone, string> = {
  published: 'var(--ink-green)',
  edited: 'var(--ink-accent)',
  waiting: 'var(--ink-amber)',
  scheduled: 'var(--ink-accent)',
  down: 'var(--ink-ink3)',
};
const EDIT_NOUN: Record<string, string> = {
  article: 'article edit',
  page: 'page edit',
  site: 'site edit',
  form: 'form edit',
  media: 'media change',
  social: 'post edit',
};

interface Mark {
  at: number;
  tone: Tone;
  count: number;
  noun: string;
}

interface Cluster {
  at: number;
  tone: Tone;
  count: number;
  label: string;
}

/** Same-tone marks close together become one bar ("24 published, 8:43 AM"). */
function cluster(marks: Mark[], gapMs: number): Cluster[] {
  const out: Array<Cluster & { nouns: Map<string, number> }> = [];
  const open = new Map<Tone, Cluster & { nouns: Map<string, number> }>();
  for (const m of [...marks].sort((a, b) => a.at - b.at)) {
    const c = open.get(m.tone);
    if (c && m.at - c.at <= gapMs) {
      c.count += m.count;
      c.at = m.at;
      c.nouns.set(m.noun, (c.nouns.get(m.noun) || 0) + m.count);
    } else {
      const next = { at: m.at, tone: m.tone, count: m.count, label: '', nouns: new Map() };
      next.nouns.set(m.noun, m.count);
      open.set(m.tone, next);
      out.push(next);
    }
  }
  return out.map(({ nouns, ...c }) => {
    const label =
      c.tone === 'edited'
        ? nouns.size === 1
          ? plural(c.count, [...nouns.keys()][0])
          : plural(c.count, 'edit')
        : `${c.count} ${[...nouns.keys()][0]}`;
    return { ...c, label };
  });
}

const Ribbon: React.FC<{
  span: Span;
  from: Date;
  to: Date;
  clusters: Cluster[];
  waitingLabel: string | null;
  next: { at: Date; count: number } | null;
}> = ({ span, from, to, clusters, waitingLabel, next }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(1000);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setWidth(el.clientWidth || 1000));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const now = new Date();
  const total = to.getTime() - from.getTime();
  const pct = (t: number) => Math.min(100, Math.max(0, ((t - from.getTime()) / total) * 100));
  const nowPct = pct(now.getTime());
  const maxCount = Math.max(1, ...clusters.map((c) => c.count));
  const height = (n: number) => Math.round(18 + 92 * Math.min(1, n / maxCount));

  const ticks =
    span === 'day'
      ? [0, 6, 12, 18].map((h) => ({
          left: (h / 24) * 100,
          label: h === 0 ? '12 AM' : h === 12 ? '12 PM' : `${h % 12} ${h < 12 ? 'AM' : 'PM'}`,
        }))
      : Array.from({ length: 7 }, (_, i) => {
          const d = new Date(from.getTime() + i * DAY);
          return { left: (i / 7) * 100, label: `${weekday(d)} ${d.getDate()}` };
        });

  // Label the biggest bars that have room; "N waiting on you" always gets one.
  // A label hangs right of its bar (left of it past the middle) and flips
  // sides rather than run into another; one that fits neither way is left off.
  const labelText = (c: Cluster) => (c.tone === 'waiting' ? waitingLabel : c.label) || '';
  const sideOf = new Map<Cluster, 'left' | 'right'>();
  const taken: Array<[number, number]> = [];
  const fits = (a: number, b: number) =>
    a >= 0 && b <= width && taken.every(([x, y]) => b + 10 <= x || a >= y + 10);
  const tryLabel = (c: Cluster) => {
    const x = (pct(c.at) / 100) * width;
    const w = Math.max(labelText(c).length * 7, 64) + 10;
    const order: Array<'left' | 'right'> = x > width * 0.55 ? ['left', 'right'] : ['right', 'left'];
    for (const side of order) {
      const [a, b] = side === 'right' ? [x, x + w] : [x - w, x];
      if (fits(a, b)) {
        taken.push([a, b]);
        sideOf.set(c, side);
        return;
      }
    }
  };
  const amber = clusters.filter((c) => c.tone === 'waiting');
  const lastAmber = amber[amber.length - 1];
  // The "now" pill and the next-scheduled note sit above the bars; keep clear.
  if (next) taken.push([width - 130, width]);
  if (lastAmber && waitingLabel) tryLabel(lastAmber);
  for (const c of [...clusters].sort((a, b) => b.count - a.count)) {
    if (sideOf.size >= 6 || c.tone === 'waiting') continue;
    tryLabel(c);
  }

  // Bars at the same spot sit side by side rather than on top of each other.
  const placed: Array<{ c: Cluster; left: number; shift: number }> = [];
  for (const c of clusters) {
    const left = pct(c.at);
    const shift = placed.filter((p) => Math.abs(p.left - left) < 0.4).length * 8;
    placed.push({ c, left, shift });
  }

  return (
    <>
      <div className="today-ribbon" ref={ref}>
        <div className="today-future" style={{ left: `${nowPct}%` }} />
        {ticks.map((t) => (
          <React.Fragment key={t.label}>
            <div className="today-tick" style={{ left: `${t.left}%` }} />
            <div className="today-tick-label ink-mono" style={{ left: `${t.left}%` }}>
              {t.label}
            </div>
          </React.Fragment>
        ))}
        {placed.map(({ c, left, shift }, i) => {
          const h = height(c.count);
          const side = sideOf.get(c);
          const label = labelText(c);
          const at = new Date(c.at);
          return (
            <React.Fragment key={`${c.tone}-${c.at}-${i}`}>
              <div
                className="today-bar"
                title={`${label || c.label} · ${span === 'week' ? `${weekday(at)} ` : ''}${time(at)}`}
                style={{
                  left: `calc(${left}% + ${shift}px)`,
                  height: h,
                  background: TONE_COLOR[c.tone],
                }}
              />
              {side && label ? (
                <div
                  className="today-bar-label"
                  data-side={side}
                  style={{
                    left: `calc(${left}% + ${shift}px)`,
                    bottom: h + 8,
                    color:
                      c.tone === 'waiting'
                        ? 'var(--ink-amber)'
                        : c.tone === 'published'
                          ? 'var(--ink-ink)'
                          : 'var(--ink-ink2)',
                  }}
                >
                  {label}
                  <div className="ink-mono">
                    {span === 'week' ? `${weekday(at)} ` : ''}
                    {time(at)}
                  </div>
                </div>
              ) : null}
            </React.Fragment>
          );
        })}
        <div className="today-now" style={{ left: `${nowPct}%` }} />
        <div className="today-now-pill ink-mono" style={{ left: `${nowPct}%` }}>
          {span === 'week' ? `${weekday(now)} ` : ''}
          {time(now)}
        </div>
        {next ? (
          <Link to="/content" className="today-next">
            {when(next.at)}
            <div>{plural(next.count, 'scheduled', 'scheduled')} →</div>
          </Link>
        ) : null}
      </div>
    </>
  );
};

// ── Queue ───────────────────────────────────────────────────────────────────

interface QueueCard {
  key: string;
  chip: string;
  corner: string;
  preview: string;
  image?: string | null;
  title: string;
  note?: string | null;
  check?: { text: string; tone: 'ok' | 'warn' | 'block' } | null;
  reviewTo: string;
  cta: string;
  blocked?: boolean;
  approve: () => Promise<void>;
}

const CHECK_COLOR = {
  ok: 'var(--ink-green-ink)',
  warn: 'var(--ink-amber)',
  block: 'var(--ink-red)',
};

const Card: React.FC<{ card: QueueCard; busy: boolean; onApprove: () => void }> = ({
  card,
  busy,
  onApprove,
}) => (
  <div className="ink-card today-card">
    <div className="today-card-preview">
      {card.image ? <img src={card.image} alt="" /> : <span>{card.preview}</span>}
      <span className="today-chip">{card.chip}</span>
      <span className="today-corner ink-mono">{card.corner}</span>
    </div>
    <div className="today-card-body">
      <div className="today-card-title">{card.title}</div>
      {card.note ? <div className="today-card-note">{card.note}</div> : null}
      {card.check ? (
        <div className="today-card-check" style={{ color: CHECK_COLOR[card.check.tone] }}>
          {card.check.text}
        </div>
      ) : null}
      <div className="today-card-actions">
        <Link to={card.reviewTo} className="ink-btn">
          Review
        </Link>
        <button
          type="button"
          className="ink-btn ink-btn-primary"
          onClick={onApprove}
          disabled={busy || card.blocked}
          title={card.blocked ? 'Fix the blocking issue first (Review)' : undefined}
        >
          {card.cta}
        </button>
      </div>
    </div>
  </div>
);

function checkLine(w: TodayWaiting): QueueCard['check'] {
  const c = w.checks;
  if (!c) return null;
  const more = c.more > 0 ? ` (+${c.more} more)` : '';
  if (c.blocking) return { tone: 'block', text: `● ${c.first}${more}` };
  if (c.warnings) return { tone: 'warn', text: `● ${c.first}${more}` };
  return { tone: 'ok', text: '✓ Checks passed' };
}

const CHIP: Record<TodayKind, [string, string]> = {
  article: ['Publish', 'Update'],
  page: ['New page', 'Page change'],
  form: ['New form', 'Form change'],
  site: ['Site change', 'Site change'],
};

// ── Page ────────────────────────────────────────────────────────────────────

export const CmsToday: React.FC = () => {
  const { accountId } = useAccount();
  const [span, setSpan] = useState<Span>('day');
  const [showAll, setShowAll] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const {
    window: win,
    loading,
    error,
    data,
    waitingPosts,
    posts,
    campaigns,
    need,
    refetch,
  } = useToday(span);

  const [publish] = useMutation(PUBLISH_TINYCMS_CONTENT);
  const [unpublish] = useMutation(UNPUBLISH_TINYCMS_CONTENT);
  const [approvePost] = useMutation(APPROVE_SOCIAL_POST);
  const [approveCampaign] = useMutation(APPROVE_CAMPAIGN);
  const contentLists = ['GetTinycmsArticles', 'GetTinycmsPages', 'GetTinycmsForms'];

  const from = useMemo(() => new Date(win.from), [win.from]);
  const to = useMemo(() => new Date(win.to), [win.to]);
  const approvals = useMemo(() => data?.approvals || [], [data]);
  const agent = useMemo(() => data?.agent || [], [data]);
  const sent = useMemo(
    () =>
      posts.filter((p) => {
        const t = toDate(p.socialPublishDate);
        return (p.reachStatus === 'posted' || p.reachStatus === 'publishing') && t && t >= from;
      }),
    [posts, from]
  );

  // ── Cards ─────────────────────────────────────────────────────────────────
  const cards: QueueCard[] = useMemo(() => {
    const content = (data?.waiting || []).map((w): QueueCard => {
      const title = w.title || (w.kind === 'site' ? 'Site look and navigation' : 'Untitled');
      return {
        key: `${w.kind}:${w.id}`,
        chip: CHIP[w.kind][w.live ? 1 : 0],
        corner: ago(w.updatedAt),
        preview: w.path || (w.kind === 'site' ? 'look + navigation' : `${w.kind} preview`),
        image: w.heroUrl,
        title,
        note: w.note,
        check: checkLine(w),
        reviewTo: reviewHref(w.kind, w.id),
        cta: 'Approve',
        blocked: Boolean(w.checks?.blocking),
        approve: async () => {
          if (
            !window.confirm(
              `Approve and publish “${title}” now?\n\nVersion ${w.workingHash.slice(0, 10)} — your approval is recorded with your name and the time.`
            )
          )
            return;
          const { data: res } = await publish({
            variables: {
              accountId,
              kind: w.kind,
              id: w.kind === 'site' ? undefined : w.id,
              action: 'publish',
              contentHash: w.workingHash,
            },
            refetchQueries: contentLists,
          });
          const r = res?.publishTinycmsContent;
          if (r?.ok) showInkToast(`Published “${title}”.`);
          else
            showInkToast(`Not published: ${r?.message || r?.refused || 'refused'}. Open Review.`);
        },
      };
    });

    const camps = campaigns.map((c: TodayCampaign): QueueCard => {
      const waiting = c.posts.filter((p) => p.status === 'in_review');
      const dates = waiting
        .map((p) => toDate(p.scheduledDate))
        .filter(Boolean)
        .sort((a, b) => a!.getTime() - b!.getTime()) as Date[];
      const short = (d: Date) => d.toLocaleDateString([], { month: 'short', day: 'numeric' });
      const end = toDate(c.deadline) || dates[dates.length - 1];
      const runs = dates[0] ? `Runs ${short(dates[0])}${end ? ` – ${short(end)}` : ''}.` : '';
      return {
        key: `campaign:${c.id}`,
        chip: 'Reach campaign',
        corner: ago(c.createdAt),
        preview: `${c.landingPageUrl ? 'landing page + ' : ''}${plural(waiting.length, 'post')}`,
        title: c.name,
        note: `${plural(waiting.length, 'post')} waiting.${runs ? ` ${runs}` : ''}`,
        check: null,
        reviewTo: `/projects?campaign=${c.id}`,
        cta: 'Approve campaign',
        approve: async () => {
          if (!window.confirm(`Approve the “${c.name}” campaign? Its posts go on the calendar.`))
            return;
          const { data: res } = await approveCampaign({ variables: { id: c.id } });
          const r = res?.approveCampaign;
          showInkToast(
            `Approved. ${r?.scheduled || 0} on the calendar.${
              r?.undated ? ` ${r.undated} need a date before going out.` : ''
            }`
          );
        },
      };
    });

    const byChannel = new Map<string, ReachPost[]>();
    for (const p of waitingPosts) {
      const k = (p.platform || '').toLowerCase();
      byChannel.set(k, [...(byChannel.get(k) || []), p]);
    }
    const postCards = [...byChannel].map(([platform, list]): QueueCard => {
      const times = list.map(postTime).filter(Boolean) as Date[];
      const first = times.sort((a, b) => a.getTime() - b.getTime())[0];
      const last = times[times.length - 1];
      const daysOut = first && last ? Math.ceil((last.getTime() - Date.now()) / DAY) : 0;
      return {
        key: `posts:${platform}`,
        chip: 'Reach posts',
        corner: !first
          ? ''
          : list.length > 1 && daysOut > 1
            ? `next ${daysOut} days`
            : when(first).toLowerCase(),
        preview: plural(list.length, `${channelName(platform)} post`),
        title: plural(
          list.length,
          `post for ${channelName(platform)}`,
          `posts for ${channelName(platform)}`
        ),
        note: list
          .slice(0, 3)
          .map((p) => {
            const t = postTime(p);
            return `${t ? `${when(t)}: ` : ''}${postText(p)}`;
          })
          .join(' · '),
        check: null,
        reviewTo: '/calendar',
        cta: list.length > 1 ? `Approve all ${list.length}` : 'Approve',
        approve: async () => {
          for (const p of list) await approvePost({ variables: { id: p.id } });
          showInkToast(`Approved ${plural(list.length, 'post')} for ${channelName(platform)}.`);
        },
      };
    });

    return [...content, ...camps, ...postCards];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, campaigns, waitingPosts, accountId]);

  const run = async (card: QueueCard) => {
    setBusy(card.key);
    try {
      await card.approve();
      await refetch();
    } catch (err) {
      showInkToast(err instanceof Error ? err.message : 'That didn’t work.');
    } finally {
      setBusy(null);
    }
  };

  // ── Ribbon data ───────────────────────────────────────────────────────────
  const clusters = useMemo(() => {
    const marks: Mark[] = [];
    for (const a of approvals) {
      const at = new Date(a.recordedAt).getTime();
      if (a.action === 'publish')
        marks.push({ at, tone: 'published', count: 1, noun: 'published' });
      else if (a.action === 'schedule')
        marks.push({ at, tone: 'scheduled', count: 1, noun: 'scheduled' });
      else marks.push({ at, tone: 'down', count: 1, noun: 'taken down' });
    }
    for (const g of agent) {
      marks.push({
        at: new Date(g.at).getTime(),
        tone: 'edited',
        count: g.count,
        noun: EDIT_NOUN[g.group] || 'edit',
      });
    }
    for (const p of sent) {
      const t = toDate(p.socialPublishDate);
      if (t) marks.push({ at: t.getTime(), tone: 'published', count: 1, noun: 'published' });
    }
    for (const w of data?.waiting || []) {
      const t = toDate(w.updatedAt);
      if (t && t >= from)
        marks.push({ at: t.getTime(), tone: 'waiting', count: 1, noun: 'waiting' });
    }
    // Waiting from before the window still shows, at "now".
    const amberInWindow = marks.some((m) => m.tone === 'waiting');
    if (need && !amberInWindow) {
      marks.push({ at: Date.now(), tone: 'waiting', count: 1, noun: 'waiting' });
    }
    return cluster(marks, span === 'day' ? 30 * 60000 : 6 * 3600000);
  }, [approvals, agent, sent, data, from, need, span]);

  const changes = approvals.length + agent.reduce((n, g) => n + g.count, 0) + sent.length;

  const upcoming: TodayScheduled[] = useMemo(
    () => (data?.scheduled || []).filter((s) => new Date(s.at).getTime() >= Date.now()),
    [data]
  );
  const next = upcoming[0] ? { at: new Date(upcoming[0].at), count: upcoming.length } : null;

  // ── Went out ──────────────────────────────────────────────────────────────
  const wentOut = useMemo(() => {
    const rows: Array<{
      key: string;
      at: Date;
      text: string;
      obj: string;
      undo?: () => Promise<void>;
      undone?: boolean;
    }> = approvals.map((a) => ({
      key: a.id,
      at: new Date(a.recordedAt),
      text: describeApproval(a),
      obj: [
        a.subjectPath,
        a.approvedBy
          ? `${a.action === 'unpublish' ? 'by' : 'approved by'} ${a.approvedBy}${
              a.via === 'ui' ? ' here' : a.via === 'mcp' ? ' in chat with the agent' : ''
            }`
          : null,
        a.action === 'schedule' && a.scheduledFor ? `for ${when(new Date(a.scheduledFor))}` : null,
      ]
        .filter(Boolean)
        .join(' · '),
      undone: a.action === 'publish' && !a.stillLive,
      undo:
        a.action === 'publish' && a.stillLive && a.kind !== 'site'
          ? async () => {
              const name = a.subjectTitle ? `“${a.subjectTitle}”` : 'this';
              if (!window.confirm(`Take ${name} off the site? You can publish it again later.`))
                return;
              await unpublish({
                variables: {
                  accountId,
                  kind: a.kind,
                  id: a.subjectId,
                  reason: 'Undone from Today',
                },
                refetchQueries: contentLists,
              });
              showInkToast(`Took down ${name}.`);
            }
          : undefined,
    }));
    for (const p of sent) {
      rows.push({
        key: `post:${p.id}`,
        at: toDate(p.socialPublishDate)!,
        text: `Posted to ${channelName(p.platform)}`,
        obj: postText(p),
      });
    }
    return rows.sort((a, b) => b.at.getTime() - a.at.getTime());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [approvals, sent, accountId]);

  const undo = async (key: string, fn: () => Promise<void>) => {
    setBusy(key);
    try {
      await fn();
      await refetch();
    } catch (err) {
      showInkToast(err instanceof Error ? err.message : 'That didn’t work.');
    } finally {
      setBusy(null);
    }
  };

  const stamp = (d: Date) => (span === 'week' ? `${weekday(d)} ${time(d)}` : time(d));
  const shown = showAll ? cards : cards.slice(0, QUEUE_SHOWN);
  const headA =
    loading && !data
      ? 'Today'
      : span === 'week'
        ? `${plural(changes, 'change')} this week.`
        : changes
          ? `${plural(changes, 'change')} so far.`
          : 'No changes yet today.';

  return (
    <main className="ink-body">
      <div className="today-head">
        <div>
          <div className="ink-muted">{dayLabel()}</div>
          <h1 className="ink-display">
            {headA}{' '}
            {loading && !data ? null : (
              <span style={{ color: need ? 'var(--ink-amber)' : 'var(--ink-green-ink)' }}>
                {need ? `${need} need${need === 1 ? 's' : ''} you.` : 'You’re all caught up.'}
              </span>
            )}
          </h1>
        </div>
        <div className="today-head-actions">
          <Link to="/cms/history" className="today-history">
            Approval history →
          </Link>
          <div className="ink-seg today-seg" role="tablist" aria-label="Range">
            {(['day', 'week'] as Span[]).map((s) => (
              <button
                key={s}
                type="button"
                role="tab"
                aria-selected={span === s}
                data-active={span === s}
                onClick={() => setSpan(s)}
              >
                {s === 'day' ? 'Day' : 'Week'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {error && !data ? (
        <div className="ink-muted" role="alert" style={{ marginTop: 16, color: 'var(--ink-red)' }}>
          Today couldn’t load just now. What’s waiting is still in Content.{' '}
          <button type="button" className="today-undo" onClick={() => refetch()}>
            Try again
          </button>
        </div>
      ) : null}

      <Ribbon
        span={span}
        from={from}
        to={to}
        clusters={clusters}
        waitingLabel={need ? `${need} waiting on you` : null}
        next={next}
      />
      <div className="today-legend">
        <span>
          <i style={{ background: 'var(--ink-green)' }} />
          Published
        </span>
        <span>
          <i style={{ background: 'var(--ink-accent)' }} />
          Edited / scheduled
        </span>
        <span>
          <i style={{ background: 'var(--ink-amber)' }} />
          Waiting on you
        </span>
      </div>

      {cards.length ? (
        <>
          <div className="today-queue">
            {shown.map((c) => (
              <Card key={c.key} card={c} busy={busy === c.key} onApprove={() => run(c)} />
            ))}
          </div>
          {cards.length > QUEUE_SHOWN ? (
            <button
              type="button"
              className="ink-btn ink-btn-quiet"
              style={{ marginTop: 16 }}
              onClick={() => setShowAll((v) => !v)}
            >
              {showAll ? 'Show fewer' : `Show all ${cards.length}`}
            </button>
          ) : null}
        </>
      ) : loading ? null : (
        <div className="today-empty">
          <span
            className="ink-dot"
            style={{ width: 10, height: 10, background: 'var(--ink-green)' }}
          />
          <div>
            <div style={{ font: '600 16px/1.3 var(--ink-font)' }}>Nothing waiting on you</div>
            <div className="ink-muted" style={{ marginTop: 3 }}>
              Your agent asks here before anything goes live.
            </div>
          </div>
        </div>
      )}

      <div className="today-columns">
        <div>
          <div className="today-col-title">
            {span === 'week' ? 'Went out this week' : 'Went out today'}
          </div>
          <div className="ink-muted" style={{ marginTop: 4 }}>
            Each one went live once someone approved it. You can undo any that are still live.
          </div>
          <div style={{ marginTop: 14 }}>
            {wentOut.map((e) => (
              <div key={e.key} className="today-row today-row-out">
                <span className="ink-mono today-row-time">{stamp(e.at)}</span>
                <div style={{ opacity: e.undone ? 0.55 : 1, minWidth: 0 }}>
                  <div className="today-row-text">{e.text}</div>
                  {e.obj ? <div className="today-row-obj">{e.obj}</div> : null}
                </div>
                {e.undo ? (
                  <button
                    type="button"
                    className="today-undo"
                    disabled={busy === e.key}
                    onClick={() => undo(e.key, e.undo!)}
                  >
                    Undo
                  </button>
                ) : e.undone ? (
                  <span className="today-row-obj">No longer live</span>
                ) : (
                  <span />
                )}
              </div>
            ))}
            {!wentOut.length && !loading ? (
              <div className="ink-muted">
                Nothing went out {span === 'week' ? 'this week' : 'yet today'}.
              </div>
            ) : null}
          </div>
        </div>
        <div>
          <div className="today-col-title">Coming up</div>
          <div className="ink-muted" style={{ marginTop: 4 }}>
            Scheduled to publish automatically.
          </div>
          <div style={{ marginTop: 14 }}>
            {upcoming.map((s) => (
              <Link
                key={`${s.kind}:${s.id}`}
                to={reviewHref(s.kind, s.id)}
                className="today-row today-row-up"
              >
                <span className="ink-mono" style={{ fontSize: 12, color: 'var(--ink-accent)' }}>
                  {when(new Date(s.at))}
                </span>
                <span className="today-row-text">{s.title || 'Untitled'}</span>
                <span className="today-row-cat">{s.cat || ''}</span>
              </Link>
            ))}
            {!upcoming.length && !loading ? (
              <div className="ink-muted">Nothing scheduled.</div>
            ) : null}
          </div>
        </div>
      </div>
    </main>
  );
};
